import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  FLORIDA_ADDENDUM_EFFECTIVE_DATE,
  FLORIDA_ADDENDUM_SECTIONS,
  FLORIDA_ADDENDUM_VERSION,
  PRIVACY_EFFECTIVE_DATE,
  PRIVACY_SECTIONS,
  PRIVACY_VERSION,
  TERMS_EFFECTIVE_DATE,
  TERMS_SECTIONS,
  TERMS_VERSION,
} from "./legal";

type Matter = {
  displayReference: string;
  status: string;
  state: string;
  county: string;
  court: string | null;
  creditor: string;
  debtor: string;
  caseNumber: string;
  instrumentNumber: string;
  bookPage: string | null;
  recordedAt: string;
  judgmentEnteredAt: string | null;
  deadlineAt: string;
  deadlineApplicable: boolean;
  reviewedAt: string;
  originalAmountCents: number | null;
  amountNote: string | null;
  valueEstimate: {
    estimatedValueCents: number;
    estimatedInterestCents: number;
    calculatedThrough: string;
    judgmentEnteredAt: string;
    methodology: "florida-statutory-simple-interest-v1";
    rateSourceUrl: string;
  } | null;
  sourceUrl: string;
  verificationSummary: string;
  renewalFeeCents: number;
  eligible: boolean;
  canStartIntake: boolean;
  isDemo: boolean;
  documentAvailable: boolean;
  documentName: string | null;
  documentArchivedAt: string | null;
  paymentsEnabled: boolean;
  monitoringAvailable: boolean;
  monitoringEndsAt: string | null;
  callBookingUrl: string | null;
};

type Intake = {
  representativeName: string;
  organizationName: string;
  phone: string;
  creditorAddressLine1: string;
  creditorAddressLine2: string;
  creditorCity: string;
  creditorStateCode: string;
  creditorPostalCode: string;
  authorityAttested: boolean;
  termsAccepted: boolean;
  stateAddendumAccepted: boolean;
  debtorMonitoring: boolean;
};

type PortalPayload = {
  matter: Matter;
  verified: boolean;
  emailMasked: string | null;
  accountAuthenticated: boolean;
  accountExists: boolean;
};

type AccountMatter = {
  accessId: string;
  creditor: string;
  debtor: string;
  caseNumber: string;
  state: string;
  county: string;
  deadlineAt: string;
  recordedAt: string;
  deadlineApplicable: boolean;
  orderId: string | null;
  orderStatus: string | null;
  orderUpdatedAt: string | null;
  amountCents: number | null;
  sourceUrl: string;
  sourceDocumentUrl: string | null;
  sourceDocumentName: string | null;
  sourceDocumentAvailable: boolean;
  receiptAvailable: boolean;
  unreadMessages: number;
  statusLabel: string;
  statusDetail: string;
  events: Array<{ status: string; title: string; detail: string | null; createdAt: string }>;
  documents: Array<{ id: string; name: string; type: string; createdAt: string }>;
  monitoring: null | { status: string; lastCheckedAt: string | null; nextCheckAt: string | null; currentPeriodEnd: string | null; serviceEndsAt: string | null };
};

type CaseAttachment = { id: string; name: string; mimeType: string; byteSize: number; uploadedByRole: string; scanStatus: string; visibleToClient: boolean };
type CaseMessage = { id: string; senderRole: string; sender: string; body: string; createdAt: string; attachments: CaseAttachment[] };

type AccountPayload = {
  account: { email: string; type: "client" | "attorney" };
  matters: AccountMatter[];
};

type StaffOrderSummary = {
  id: string;
  status: string;
  amountCents: number;
  representativeName: string;
  organizationName: string;
  email: string;
  creditor: string;
  debtor: string;
  caseNumber: string;
  county: string;
  state: string;
  deadlineAt: string;
  createdAt: string;
  updatedAt: string;
};

type StaffOrderDetail = {
  staff: { role: "operator" | "admin" };
  order: StaffOrderSummary & {
    phone: string;
    creditorAddress: string;
    creditorAddressLine1: string | null;
    creditorAddressLine2: string | null;
    creditorCity: string | null;
    creditorStateCode: string | null;
    creditorPostalCode: string | null;
    termsVersion: string | null;
    termsAcceptedAt: string | null;
    termsSha256: string | null;
    privacyVersion: string | null;
    stateAddendumCode: string | null;
    stateAddendumVersion: string | null;
    stateAddendumSha256: string | null;
    stripeSessionId: string | null;
    stripePaymentIntentId: string | null;
    stripeRefundId: string | null;
    notarizationPlan: {
      status: "supported" | "blocked";
      provider: "onenotary";
      stateCode: string;
      county: string;
      sessionType: "ron" | "travel_paper" | null;
      execution: "remote_online" | "in_person_paper" | null;
      filingMedium: "electronic" | "paper" | null;
      reason: string;
    };
  };
  nextStatuses: string[];
  events: Array<Record<string, string | number | null>>;
  notes: Array<Record<string, string | number | null>>;
  documents: Array<Record<string, string | number | null>>;
  messages: CaseMessage[];
};

type StaffDashboard = {
  metrics: {
    totalJudgments: number; noticeReady: number; lettersSent: number; activeOrders: number;
    unreadMessages: number; filesPending: number; verifiedToPaidRate: number; checkoutConversionRate: number;
    verifiedSessions: number; paidOrders: number; startedOrders: number; revenueCents: number;
  };
  judgments: Array<{
    reference: string; creditor: string; debtor: string; caseNumber: string; state: string; county: string;
    deadlineAt: string; matterStatus: string; eligible: boolean; orderId: string | null; orderStatus: string | null;
    orderUpdatedAt: string | null; unreadMessages: number; noticeStatus: string; noticeSentAt: string | null;
  }>;
};

type StaffAlert = {
  alert_key: string;
  category: string;
  severity: "warning" | "critical";
  summary: string;
  detected_count: number;
  status: "open" | "resolved";
  first_detected_at: string;
  last_detected_at: string;
  resolved_at: string | null;
};

const initialIntake: Intake = {
  representativeName: "",
  organizationName: "",
  phone: "",
  creditorAddressLine1: "",
  creditorAddressLine2: "",
  creditorCity: "",
  creditorStateCode: "",
  creditorPostalCode: "",
  authorityAttested: false,
  termsAccepted: false,
  stateAddendumAccepted: false,
  debtorMonitoring: false,
};

const US_REGIONS = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"],
  ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"], ["DC", "District of Columbia"], ["FL", "Florida"],
  ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
  ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
  ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"], ["MO", "Missouri"],
  ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"],
  ["NM", "New Mexico"], ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"],
  ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"],
  ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"],
  ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
  ["AS", "American Samoa"], ["GU", "Guam"], ["MP", "Northern Mariana Islands"], ["PR", "Puerto Rico"],
  ["VI", "U.S. Virgin Islands"], ["AA", "Armed Forces Americas"], ["AE", "Armed Forces Europe"], ["AP", "Armed Forces Pacific"],
] as const;

function formatReferenceInput(value: string) {
  const compact = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!compact) return "";
  if (compact === "J") return "J";
  const body = (compact.startsWith("JL") ? compact.slice(2) : compact).slice(0, 12);
  if (!body) return "JL";
  return `JL-${body.match(/.{1,4}/g)?.join("-") || ""}`;
}

function formatPhoneInput(value: string) {
  let digits = value.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <a className={`brand ${compact ? "brand--compact" : ""}`} href="/" aria-label="JudgmentLock home">
      <span className="brand__mark" aria-hidden="true">
        <svg viewBox="0 0 64 64">
          <path className="brand__shackle" d="M20.5 29v-6.5a11.5 11.5 0 0 1 23 0V29" />
          <rect className="brand__body" x="14" y="27" width="36" height="27" rx="8" />
          <path className="brand__key" d="M32 34.5a4.75 4.75 0 0 0-2.25 8.94L28.4 49h7.2l-1.35-5.56A4.75 4.75 0 0 0 32 34.5Z" />
        </svg>
      </span>
      <span className="brand__wordmark"><span>Judgment</span><strong>Lock</strong></span>
    </a>
  );
}

function ArrowIcon() {
  return <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h12M11 5l5 5-5 5" /></svg>;
}

function CheckIcon() {
  return <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m4 10 4 4 8-9" /></svg>;
}

function LockIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10V7a5 5 0 0 1 10 0v3M5 10h14v11H5z" /></svg>;
}

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);

  return (
    <header className="site-header">
      <div className="shell header-inner">
        <Brand />
        <nav className="desktop-nav" aria-label="Primary navigation">
          <a href="/services">Services</a>
          <a href="/#how-it-works">How it works</a>
          <a href="/#faq">FAQ</a>
          <a href="/firms">For law firms</a>
        </nav>
        <a className="button button--small button--ink" href="/login">Client login</a>
        <button
          className={`mobile-menu-toggle${menuOpen ? " is-open" : ""}`}
          type="button"
          aria-expanded={menuOpen}
          aria-controls="mobile-navigation"
          aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span /><span /><span />
        </button>
        {menuOpen && <>
          <button className="mobile-menu-scrim" type="button" aria-label="Close navigation menu" onClick={() => setMenuOpen(false)} />
          <nav className="mobile-menu" id="mobile-navigation" aria-label="Mobile navigation">
            <a href="/services" onClick={() => setMenuOpen(false)}>Services</a>
            <a href="/#how-it-works" onClick={() => setMenuOpen(false)}>How it works</a>
            <a href="/#faq" onClick={() => setMenuOpen(false)}>FAQ</a>
            <a href="/firms" onClick={() => setMenuOpen(false)}>For law firms</a>
            <a className="button button--ink button--wide" href="/login" onClick={() => setMenuOpen(false)}>Client login <ArrowIcon /></a>
          </nav>
        </>}
      </div>
    </header>
  );
}

function ReferenceLookup({
  formId = "lookup",
  inputId = "reference",
  label = "Notice reference",
  buttonLabel = "View my judgment details",
  help = "Look for the code beginning with JL- on your notice or email.",
}: {
  formId?: string;
  inputId?: string;
  label?: string;
  buttonLabel?: string;
  help?: string;
} = {}) {
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function lookup(event: FormEvent) {
    event.preventDefault();
    const normalized = reference.trim().toUpperCase();
    if (!normalized) return;
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/lookup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reference: normalized }),
      });
      const payload = await response.json() as { matched?: boolean; error?: string };
      if (!response.ok || !payload.matched) throw new Error(payload.error || "We couldn't open a record with that reference.");
      window.location.assign("/portal");
    } catch (err) {
      setError(err instanceof Error ? err.message : "We could not locate that reference.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="lookup" onSubmit={lookup} id={formId}>
      <label htmlFor={inputId}>{label}</label>
      <div className="lookup__row">
        <div className="input-wrap">
          <LockIcon />
          <input
            id={inputId}
            value={reference}
            onChange={(event) => setReference(formatReferenceInput(event.target.value))}
            placeholder="JL-XXXX-XXXX-XXXX"
            maxLength={17}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck="false"
            aria-describedby={error ? `${inputId}-error` : `${inputId}-help`}
          />
        </div>
        <button className="button button--gold" type="submit" disabled={loading}>
          {loading ? "Opening…" : buttonLabel} {!loading && <ArrowIcon />}
        </button>
      </div>
      {error ? <p className="form-error" id={`${inputId}-error`}>{error}</p> : <p className="lookup__help" id={`${inputId}-help`}>{help}</p>}
    </form>
  );
}

function LoginForm({ onSuccess }: { onSuccess?: () => void } = {}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch("/api/account/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const payload = await response.json() as { authenticated?: boolean; next?: string; error?: string };
      if (!response.ok || !payload.authenticated) throw new Error(payload.error || "Email or password is incorrect.");
      if (onSuccess) onSuccess();
      else window.location.assign(payload.next || "/account");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Email or password is incorrect.");
      setSubmitting(false);
    }
  }

  return (
    <form className="account-form" onSubmit={submit}>
      <label><span>Email address</span><input type="email" required autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
      <label><span>Password</span><input type="password" required autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
      {error && <p className="form-error form-error--dark">{error}</p>}
      <button className="button button--gold button--wide" type="submit" disabled={submitting}>{submitting ? "Signing in…" : "Sign in securely"} <ArrowIcon /></button>
      <a className="account-form__link" href="/forgot-password">Forgot password?</a>
    </form>
  );
}

function ClientLoginPage() {
  return (
    <div className="client-login-page">
      <Header />
      <main className="client-login-main">
        <div className="shell client-login-shell">
          <section className="client-login-copy">
            <p className="eyebrow"><span /> Secure client access</p>
            <h1>Track your judgments and filing work.</h1>
            <p>Sign in to review linked judgments, monitor filing status, and retrieve completed recording evidence.</p>
            <div className="client-login-points">
              <span><LockIcon /> Password-protected client account</span>
              <span><CheckIcon /> Matter and filing status in one place</span>
              <span><CheckIcon /> Account creation follows verified intake</span>
            </div>
          </section>
          <section className="hero__panel client-login-panel">
            <div className="panel-kicker">Client login</div>
            <h2>Sign in</h2>
            <p>Your email address is your username. Accounts are created during verified intake.</p>
            <LoginForm />
            <div className="privacy-note"><LockIcon /> Have a notice but no account? <a href="/#notice">Open your notice first.</a></div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    await fetch("/api/account/password-reset/request", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    setSubmitting(false);
    setSent(true);
  }

  return (
    <div className="client-login-page"><Header /><main className="client-login-main"><div className="shell account-single">
      <section className="hero__panel client-login-panel">
        <div className="panel-kicker">Account recovery</div>
        <h2>Reset your password</h2>
        {sent ? <><p>If an active account matches that email, a one-time reset link is on its way. The link expires in 30 minutes.</p><a className="button button--gold button--wide" href="/login">Return to sign in</a></> : <>
          <p>Enter the email address used for your JudgmentLock account.</p>
          <form className="account-form" onSubmit={submit}>
            <label><span>Email address</span><input type="email" required autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
            <button className="button button--gold button--wide" type="submit" disabled={submitting}>{submitting ? "Sending…" : "Send reset link"} <ArrowIcon /></button>
            <a className="account-form__link" href="/login">Back to sign in</a>
          </form>
        </>}
      </section>
    </div></main><Footer /></div>
  );
}

function ResetPasswordPage() {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [complete, setComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) return setError("Passwords do not match.");
    setSubmitting(true);
    setError("");
    const response = await fetch("/api/account/password-reset/confirm", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    });
    const payload = await response.json() as { reset?: boolean; error?: string };
    setSubmitting(false);
    if (!response.ok || !payload.reset) return setError(payload.error || "This reset link is invalid or expired.");
    setComplete(true);
  }

  return (
    <div className="client-login-page"><Header /><main className="client-login-main"><div className="shell account-single">
      <section className="hero__panel client-login-panel">
        <div className="panel-kicker">Secure password reset</div>
        <h2>{complete ? "Password updated" : "Choose a new password"}</h2>
        {complete ? <><p>Your existing account sessions were signed out. Use the new password to sign in.</p><a className="button button--gold button--wide" href="/login">Sign in</a></> : <>
          <p>Use at least 12 characters. A longer passphrase is encouraged.</p>
          <form className="account-form" onSubmit={submit}>
            <label><span>New password</span><input type="password" minLength={12} maxLength={128} required autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
            <label><span>Confirm new password</span><input type="password" minLength={12} maxLength={128} required autoComplete="new-password" value={confirm} onChange={(event) => setConfirm(event.target.value)} /></label>
            {error && <p className="form-error form-error--dark">{error}</p>}
            <button className="button button--gold button--wide" type="submit" disabled={submitting || !token}>{submitting ? "Updating…" : "Update password"} <ArrowIcon /></button>
          </form>
        </>}
      </section>
    </div></main><Footer /></div>
  );
}

function Hero() {
  return (
    <main>
      <section className="hero">
        <div className="hero__wash" aria-hidden="true" />
        <div className="shell hero__grid">
          <div className="hero__copy">
            <p className="eyebrow"><span /> Judgment-lien filing, made simpler</p>
            <h1>We handle the filing so you don’t have to.</h1>
            <p className="hero__lede">JudgmentLock monitors the public record, checks the deadline, coordinates the required paperwork, submits the filing, and sends you the completed recording evidence.</p>
            <div className="hero__checks">
              <span><CheckIcon /> We confirm the record and deadline</span>
              <span><CheckIcon /> We handle the documents and fees</span>
              <span><CheckIcon /> You receive the completed filing evidence</span>
            </div>
          </div>
          <div className="hero__panel" id="notice">
            <div className="panel-kicker">Received a JudgmentLock notice?</div>
            <h2>See the judgment we found</h2>
            <p>Enter your notice reference to review the public record, see the apparent deadline, and learn what we can handle for you.</p>
            <ReferenceLookup />
            <div className="privacy-note"><LockIcon /> Free to review. No payment or personal information is required, and opening the record does not authorize service.</div>
          </div>
        </div>
      </section>
      <section className="proof-strip">
        <div className="shell proof-strip__inner">
          <div><strong>Official sources</strong><span>Court and recorder data</span></div>
          <div><strong>State-specific</strong><span>Deadline rules mapped</span></div>
          <div><strong>Evidence first</strong><span>Every matter source-linked</span></div>
          <div><strong>Nationwide design</strong><span>County-by-county coverage</span></div>
        </div>
      </section>
    </main>
  );
}

function formatDate(value: string) {
  const normalized = value.length === 10
    ? `${value}T00:00:00Z`
    : /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
      ? `${value.replace(" ", "T")}Z`
      : value;
  const instant = new Date(normalized);
  return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" }).format(instant);
}

function formatMoney(cents: number | null) {
  if (cents === null) return "Review required";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(cents / 100);
}

function addUtcYears(value: string, years: number) {
  const instant = new Date(`${value}T00:00:00Z`);
  instant.setUTCFullYear(instant.getUTCFullYear() + years);
  return instant.toISOString().slice(0, 10);
}

function daysUntilLocalDate(value: string) {
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.ceil((Date.parse(`${value}T00:00:00Z`) - today) / 86_400_000);
}

function expiryLabel(days: number) {
  if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
  if (days === 0) return "Expires today";
  return `Expires in ${days} day${days === 1 ? "" : "s"}`;
}

function PortalHeader({ onExit }: { onExit: () => void }) {
  return (
    <header className="portal-header">
      <div className="shell portal-header__inner">
        <Brand />
        <div className="portal-header__context"><strong>Secure portal</strong></div>
        <nav aria-label="Session navigation">
          <a href="mailto:support@judgementlock.com">Help</a>
          <button type="button" onClick={onExit}>Exit</button>
        </nav>
      </div>
    </header>
  );
}

function PortalProgress({ verified, accountAuthenticated, paymentStep }: { verified: boolean; accountAuthenticated: boolean; paymentStep: boolean }) {
  const current = paymentStep ? 5 : accountAuthenticated ? 4 : verified ? 3 : 2;
  const currentLabel = current === 5 ? "Make Payment" : current === 4 ? "Confirm Details" : current === 3 ? "Create Account" : "Verify Email";
  return (
    <div className="portal-progress" aria-label={`Step ${current} of 5`}>
      <div className="portal-progress__mobile"><strong>Step {current} of 5</strong><span>{currentLabel}</span></div>
      {[
        ["01", "Review Record"],
        ["02", "Verify Email"],
        ["03", "Create Account"],
        ["04", "Confirm Details"],
        ["05", "Make Payment"],
      ].map(([number, label], index) => {
        const step = index + 1;
        const complete = step < current;
        return <div className={`${step <= current ? "is-active" : ""}${complete ? " is-complete" : ""}`} aria-current={step === current ? "step" : undefined} key={number}><span>{complete ? <CheckIcon /> : number}</span><strong>{label}</strong></div>;
      })}
    </div>
  );
}

function VerificationStep({ onVerifiedState }: { onVerifiedState: (masked: string) => void }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSending(true);
    setError("");
    try {
      const response = await fetch("/api/portal/email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const payload = await response.json() as { emailMasked?: string; error?: string };
      if (!response.ok || !payload.emailMasked) throw new Error(payload.error || "The verification email could not be sent.");
      onVerifiedState(payload.emailMasked);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The verification email could not be sent.");
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="portal-step-card" aria-labelledby="verification-title">
      <p className="eyebrow"><span /> No payment yet</p>
      <h2 id="verification-title">Where should we send your secure link?</h2>
      <p>We use email verification to protect your information and keep your filing updates in one place.</p>
      <form onSubmit={submit} className="verification-form">
        <label><span>Email address</span><input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" placeholder="name@example.com" /></label>
        {error && <p className="form-error form-error--dark">{error}</p>}
        <button className="button button--gold" type="submit" disabled={sending}>{sending ? "Sending secure link…" : "Email my secure link"} <ArrowIcon /></button>
      </form>
      <p className="portal-step-note"><LockIcon /> The link expires in 20 minutes. You are not authorizing service or making a payment.</p>
    </section>
  );
}

function CheckEmail({ masked, onChange }: { masked: string; onChange: () => void }) {
  return (
    <section className="portal-step-card portal-step-card--sent" aria-live="polite">
      <div className="success-check"><CheckIcon /></div>
      <p className="eyebrow"><span /> Verification sent</p>
      <h2>Check {masked}</h2>
      <p>Open the one-time link in the email to return here and continue. Nothing has been authorized and no charge has been created.</p>
      <button className="text-button" type="button" onClick={onChange}>Use a different email</button>
    </section>
  );
}

function AccountSetup({ accountExists, emailMasked, onAuthenticated }: { accountExists: boolean; emailMasked: string | null; onAuthenticated: () => void }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function register(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) return setError("Passwords do not match.");
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch("/api/account/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const payload = await response.json() as { authenticated?: boolean; error?: string };
      if (!response.ok || !payload.authenticated) throw new Error(payload.error || "Your account could not be created.");
      onAuthenticated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Your account could not be created.");
      setSubmitting(false);
    }
  }

  return (
    <section className="portal-step-card" aria-labelledby="account-title">
      <p className="eyebrow"><span /> Verified email {emailMasked}</p>
      <h2 id="account-title">{accountExists ? "Sign in to continue with this judgment." : "Create your secure JudgmentLock account."}</h2>
      <p>{accountExists ? "An account already uses this verified email. Sign in to continue." : "Use your account to track this judgment, follow every filing update, and receive the completed proof."}</p>
      {accountExists ? <LoginForm onSuccess={onAuthenticated} /> : <form className="account-form account-form--light" onSubmit={register}>
        <label><span>Create password</span><input type="password" minLength={12} maxLength={128} required autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        <label><span>Confirm password</span><input type="password" minLength={12} maxLength={128} required autoComplete="new-password" value={confirm} onChange={(event) => setConfirm(event.target.value)} /></label>
        <small>Use at least 12 characters. A longer passphrase is encouraged.</small>
        {error && <p className="form-error form-error--dark">{error}</p>}
        <button className="button button--gold button--wide" type="submit" disabled={submitting}>{submitting ? "Creating account…" : "Create my account"} <ArrowIcon /></button>
      </form>}
      <p className="portal-step-note"><LockIcon /> This account is created only after email verification and is linked to the matched judgment.</p>
    </section>
  );
}

function AuthorityIntake({ matter, emailMasked, paymentStep, onPaymentStepChange }: {
  matter: Matter;
  emailMasked: string | null;
  paymentStep: boolean;
  onPaymentStepChange: (active: boolean) => void;
}) {
  const [intake, setIntake] = useState(initialIntake);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const requiredAcceptancesComplete = intake.authorityAttested
    && intake.termsAccepted
    && (matter.state !== "FL" || intake.stateAddendumAccepted);

  function update<K extends keyof Intake>(key: K, value: Intake[K]) {
    setIntake((current) => ({ ...current, [key]: value }));
  }

  function reviewPayment(event: FormEvent) {
    event.preventDefault();
    setError("");
    onPaymentStepChange(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function openCheckout(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(intake),
      });
      const payload = await response.json() as { checkoutUrl?: string; error?: string };
      if (!response.ok || !payload.checkoutUrl) throw new Error(payload.error || "Secure checkout could not be opened.");
      window.location.assign(payload.checkoutUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Secure checkout could not be opened.");
      setSubmitting(false);
    }
  }

  if (paymentStep) {
    const dueToday = matter.renewalFeeCents + (intake.debtorMonitoring ? 4900 : 0);
    return (
      <section className="portal-step-card payment-step-card" aria-labelledby="payment-title">
        <button className="portal-back-link portal-back-button" type="button" onClick={() => onPaymentStepChange(false)}>← Back to your details</button>
        <p className="eyebrow"><span /> Ready for secure payment</p>
        <h2 id="payment-title">Review your order.</h2>
        <p>Your filing service is paid once. You can add annual debtor monitoring before continuing to Stripe.</p>
        <form className="intake-form" onSubmit={openCheckout}>
          <div className="payment-order-summary">
            <div><span>Judgment filing service</span><strong>{formatMoney(matter.renewalFeeCents)}</strong><small>One-time payment</small></div>
            {intake.debtorMonitoring && <div><span>Annual debtor monitoring</span><strong>$49</strong><small>Renews yearly</small></div>}
            <div className="payment-order-total"><span>Due today</span><strong>{formatMoney(dueToday)}</strong></div>
          </div>
          {matter.monitoringAvailable && <label className="monitoring-option">
            <input type="checkbox" checked={intake.debtorMonitoring} onChange={(event) => update("debtorMonitoring", event.target.checked)} />
            <span><strong>Add annual debtor monitoring — $49/year</strong><small>We’ll check monthly and alert you if a named individual debtor appears to move to another county or state. Renews automatically until canceled or {matter.monitoringEndsAt ? formatDate(matter.monitoringEndsAt) : "the judgment expires"}, whichever comes first.</small></span>
          </label>}
          {error && <p className="form-error form-error--dark">{error}</p>}
          <button className="button button--gold button--wide" type="submit" disabled={submitting || !matter.paymentsEnabled}>{submitting ? "Opening secure payment…" : "Continue to secure payment"} <ArrowIcon /></button>
          <p className="secure-line"><LockIcon /> Payment is processed by Stripe. JudgmentLock does not store card details.</p>
        </form>
      </section>
    );
  }

  return (
    <section className="portal-step-card" aria-labelledby="authority-title">
      <p className="eyebrow"><span /> Client account · {emailMasked}</p>
      <h2 id="authority-title">Tell us who is authorizing the filing.</h2>
      <p>We need the current creditor information below to prepare the filing and confirm who is authorizing it.</p>
      <form className="intake-form" onSubmit={reviewPayment}>
        <div className="field-grid">
          <label><span>Your full name</span><input required value={intake.representativeName} onChange={(event) => update("representativeName", event.target.value)} autoComplete="name" /></label>
          <label><span>Creditor / organization</span><input required value={intake.organizationName} onChange={(event) => update("organizationName", event.target.value)} autoComplete="organization" /></label>
          <label><span>Phone</span><input type="tel" required inputMode="tel" maxLength={12} pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}" title="Enter a 10-digit phone number" value={intake.phone} onChange={(event) => update("phone", formatPhoneInput(event.target.value))} autoComplete="tel" placeholder="555-555-5555" /></label>
          <label><span>Verified email</span><input value={emailMasked || "Verified"} disabled /></label>
        </div>
        <fieldset className="address-fields">
          <legend>Current creditor mailing address</legend>
          <label><span>Address</span><input required maxLength={180} value={intake.creditorAddressLine1} onChange={(event) => update("creditorAddressLine1", event.target.value)} autoComplete="address-line1" /></label>
          <label><span>Address 2 <small>(optional)</small></span><input maxLength={180} value={intake.creditorAddressLine2} onChange={(event) => update("creditorAddressLine2", event.target.value)} autoComplete="address-line2" /></label>
          <div className="address-locality-grid">
            <label className="address-city"><span>City</span><input required maxLength={100} value={intake.creditorCity} onChange={(event) => update("creditorCity", event.target.value)} autoComplete="address-level2" /></label>
            <label><span>State</span><select required value={intake.creditorStateCode} onChange={(event) => update("creditorStateCode", event.target.value)} autoComplete="address-level1"><option value="">Select</option>{US_REGIONS.map(([code]) => <option key={code} value={code}>{code}</option>)}</select></label>
            <label><span>ZIP code</span><input required maxLength={10} inputMode="numeric" pattern="[0-9]{5}(-[0-9]{4})?" title="Enter a five-digit ZIP code or ZIP+4" value={intake.creditorPostalCode} onChange={(event) => update("creditorPostalCode", event.target.value)} autoComplete="postal-code" /></label>
          </div>
        </fieldset>
        <label className="checkbox"><input type="checkbox" required checked={intake.authorityAttested} onChange={(event) => update("authorityAttested", event.target.checked)} /><span>I certify that I am the judgment creditor or am authorized to act for the creditor, and that the information supplied is current and accurate.</span></label>
        <label className="checkbox"><input type="checkbox" required checked={intake.termsAccepted} onChange={(event) => update("termsAccepted", event.target.checked)} /><span>I agree to the <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a> and acknowledge the <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>, including the service scope, refund terms, and limits of liability.</span></label>
        {matter.state === "FL" && <label className="checkbox"><input type="checkbox" required checked={intake.stateAddendumAccepted} onChange={(event) => update("stateAddendumAccepted", event.target.checked)} /><span>I have reviewed and agree to the <a href="/terms/florida" target="_blank" rel="noreferrer">Florida Service Addendum</a>, including Florida-specific filing deadlines, requirements, and limitations.</span></label>}
        {error && <p className="form-error form-error--dark">{error}</p>}
        <button className="button button--gold button--wide" type="submit" disabled={!requiredAcceptancesComplete}>Continue Checkout <ArrowIcon /></button>
        <p className="secure-line"><LockIcon /> You’ll review the price and optional monitoring before payment.</p>
      </form>
    </section>
  );
}

function PortalPage() {
  const [payload, setPayload] = useState<PortalPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    fetch("/api/portal", { headers: { Accept: "application/json" } })
      .then(async (response) => {
        const body = await response.json() as PortalPayload & { error?: string };
        if (!response.ok || !body.matter) throw new Error(body.error || "expired");
        setPayload(body);
      })
      .catch(() => setExpired(true))
      .finally(() => setLoading(false));
  }, []);

  async function exitMatter() {
    await fetch("/api/portal/logout", { method: "POST" });
    window.location.assign("/");
  }

  if (loading) return <div className="portal-loading"><Brand /><span>Opening your judgment review…</span></div>;
  if (expired || !payload) return (
    <div className="portal-expired">
      <Brand />
      <p className="eyebrow"><span /> Session ended</p>
      <h1>Re-enter the reference from your notice.</h1>
      <p>Private sessions expire automatically. No personal or payment information is available from this page.</p>
      <a className="button button--ink" href="/#notice">Open my notice <ArrowIcon /></a>
    </div>
  );

  const matter = payload.matter;
  const floridaOuterLimit = matter.state === "FL" && matter.judgmentEnteredAt ? addUtcYears(matter.judgmentEnteredAt, 20) : null;
  const floridaCurrentLienEnd = matter.state === "FL" ? addUtcYears(matter.recordedAt, 10) : null;
  const protectionDeadline = matter.deadlineApplicable ? matter.deadlineAt : floridaCurrentLienEnd;
  const daysRemaining = protectionDeadline ? daysUntilLocalDate(protectionDeadline) : null;
  const leadTimeTooShort = matter.deadlineApplicable && daysRemaining !== null && daysRemaining < 45;
  const primaryAction = payload.accountAuthenticated
    ? "Continue my order"
    : payload.verified
      ? "Create my secure account"
      : matter.deadlineApplicable
        ? "Protect my lien"
        : "Start my filing review";
  const hasOriginalAmount = matter.originalAmountCents !== null;
  const primaryFactCount = Number(matter.deadlineApplicable && protectionDeadline && daysRemaining !== null)
    + Number(hasOriginalAmount)
    + (matter.valueEstimate ? 2 : 0);

  return (
    <div className="portal-page">
      <PortalHeader onExit={exitMatter} />
      <main>
        <section className="portal-hero portal-hero--conversion">
          <div className="shell portal-shell">
            <p className="eyebrow portal-reference"><span /> We found your judgment · {matter.displayReference}</p>
            <div className="portal-conversion-grid">
              <div className="portal-conversion-copy">
                <h1>{matter.deadlineApplicable && daysRemaining !== null
                  ? daysRemaining < 0
                    ? <>The judgment lien deadline <span className="portal-headline-line">passed <em>{Math.abs(daysRemaining)} day{Math.abs(daysRemaining) === 1 ? "" : "s"} ago</em>.</span></>
                    : daysRemaining === 0
                      ? <>Your judgment lien <span className="portal-headline-line">expires <em>today</em>.</span></>
                      : <>Your judgment lien <span className="portal-headline-line">expires in <em>{daysRemaining} days</em>.</span></>
                  : hasOriginalAmount
                    ? <>Your court-filed judgment is for <span className="portal-headline-line"><em>{formatMoney(matter.originalAmountCents)}</em>.</span></>
                    : "We found your court-filed judgment."}</h1>
                <p>{matter.deadlineApplicable
                  ? "Miss this deadline and the current judgment lien ends. We handle the renewal from start to finish and send you the recorded proof."
                  : "JudgmentLock handles the paperwork, submission, tracking, and recorded proof for one flat fee."}</p>
                {matter.deadlineApplicable && <div className={`portal-value-summary portal-primary-facts portal-primary-facts--${primaryFactCount}`}>
                  <dl>
                    {matter.deadlineApplicable && protectionDeadline && daysRemaining !== null && <div className="portal-value-deadline"><dt>Current lien deadline</dt><dd>{formatDate(protectionDeadline)}</dd><small>{expiryLabel(daysRemaining)}</small></div>}
                    {hasOriginalAmount && <div><dt>Original judgment</dt><dd>{formatMoney(matter.originalAmountCents)}</dd><small>Amount at entry</small></div>}
                    {matter.valueEstimate && <div><dt>Estimated interest</dt><dd>+{formatMoney(matter.valueEstimate.estimatedInterestCents)}</dd><small>Through {formatDate(matter.valueEstimate.calculatedThrough)}</small></div>}
                    {matter.valueEstimate && <div><dt>Estimated total</dt><dd>{formatMoney(matter.valueEstimate.estimatedValueCents)}</dd><small>Before payments or credits</small></div>}
                  </dl>
                </div>}
                {matter.deadlineApplicable && <p className="portal-record-note">Deadline calculated from the public record. Later filings or corrections may change it.</p>}
              </div>

              <article className="portal-offer-card" id="service">
                <p className="eyebrow"><span /> {matter.deadlineApplicable ? "Don't let your lien expire" : "We handle the filing"}</p>
                <h2>{matter.deadlineApplicable ? "We handle the renewal from start to finish." : "We handle the filing from start to finish."}</h2>
                {matter.canStartIntake ? <>
                  <div className="portal-offer-price"><span>{matter.deadlineApplicable ? "Complete renewal service" : "Complete filing service"}</span><strong>{formatMoney(matter.renewalFeeCents)}</strong><small>One-time fee. Everything is included.</small></div>
                  <a className="button button--gold button--wide" href="/portal/start">{primaryAction} <ArrowIcon /></a>
                  <em>Next step is free. No payment yet.</em>
                  <ul>
                    <li><CheckIcon /><span><strong>We confirm the judgment, deadline, and county requirements.</strong></span></li>
                    <li><CheckIcon /><span><strong>We prepare the required copies and forms, coordinate signatures, and arrange notarization.</strong></span></li>
                    <li><CheckIcon /><span><strong>We file, track, and send your recorded proof.</strong></span></li>
                  </ul>
                </> : <div className="portal-offer-unavailable">
                  <strong>{leadTimeTooShort ? "This date is too close for our standard filing service." : "This matter is not available for online filing."}</strong>
                  <span>{leadTimeTooShort ? "JudgmentLock cannot promise timely recording. Consider contacting a Florida attorney immediately." : "You can still review the public record and official source below."}</span>
                </div>}
              </article>
            </div>

            <details className="portal-judgment-details">
              <summary>
                <span className="portal-judgment-details__heading"><strong>See the details</strong><small>Creditor, debtor, court record, and source documents</small></span>
                <span className="portal-judgment-details__toggle">
                  <span className="portal-judgment-details__expand-label">Click to expand <b aria-hidden="true">→</b></span>
                  <span className="portal-judgment-details__collapse-label">Click to collapse <b aria-hidden="true">→</b></span>
                  <i aria-hidden="true">+</i>
                </span>
              </summary>
              <div className="portal-judgment-details__body">
                <div className="portal-judgment-details__lead">
                  <div>
                    <span><CheckIcon /> Matched to the filed record</span>
                  </div>
                  <div className="document-actions">
                    {matter.documentAvailable && <a className="button button--ink document-button" href="/api/portal/document" target="_blank" rel="noreferrer">View court-filed judgment <ArrowIcon /></a>}
                    <a className="source-link" href={matter.sourceUrl} target="_blank" rel="noreferrer">View official record <ArrowIcon /></a>
                  </div>
                </div>
                <dl className="matter-grid portal-judgment-details__grid">
                  <div><dt>Judgment creditor</dt><dd>{matter.creditor}</dd></div>
                  <div><dt>Judgment debtor</dt><dd>{matter.debtor}</dd></div>
                  <div><dt>Court case</dt><dd>{matter.caseNumber}</dd><small>{matter.court || `${matter.county} County`}</small></div>
                  <div><dt>Recorded instrument</dt><dd>{matter.instrumentNumber}</dd><small>{matter.bookPage ? `Official Records ${matter.bookPage}` : "Official records"}</small></div>
                  <div><dt>Recorded</dt><dd>{formatDate(matter.recordedAt)}</dd><small>{matter.county} County, {matter.state}</small></div>
                  {matter.judgmentEnteredAt && <div><dt>Judgment entered</dt><dd>{formatDate(matter.judgmentEnteredAt)}</dd><small>Interest estimate begins after this date</small></div>}
                  <div><dt>Amount at entry</dt><dd>{formatMoney(matter.originalAmountCents)}</dd><small>Not a statement of current balance</small></div>
                  {matter.valueEstimate && <div><dt>Estimated interest</dt><dd>+{formatMoney(matter.valueEstimate.estimatedInterestCents)}</dd><small>Calculated through {formatDate(matter.valueEstimate.calculatedThrough)}</small></div>}
                  {matter.valueEstimate && <div><dt>Estimated amount</dt><dd>{formatMoney(matter.valueEstimate.estimatedValueCents)}</dd><small>If no payments or credits occurred</small></div>}
                  {protectionDeadline && daysRemaining !== null && <div className="portal-judgment-details__deadline"><dt>Time remaining</dt><dd>{expiryLabel(daysRemaining)}</dd><small>Deadline: {formatDate(protectionDeadline)}</small></div>}
                </dl>
                {matter.valueEstimate && <details className="portal-estimate-method">
                  <summary>How the estimate was calculated <span>+</span></summary>
                  <p>We applied Florida's historical statutory post-judgment interest rates through {formatDate(matter.valueEstimate.calculatedThrough)}. This assumes no payments, credits, added costs, bankruptcy effects, contractual rate, or later court orders. It is an estimate—not a payoff statement or guarantee of collection. <a href={matter.valueEstimate.rateSourceUrl} target="_blank" rel="noreferrer">View official rates</a>.</p>
                </details>}
                {floridaOuterLimit && floridaCurrentLienEnd && <div className="portal-judgment-details__lifespan">
                  <h3>How long can renewing extend the lien?</h3>
                  <dl>
                    <div><dt>Judgment entered</dt><dd>{formatDate(matter.judgmentEnteredAt!)}</dd></div>
                    <div><dt>Current filing deadline</dt><dd>{formatDate(floridaCurrentLienEnd)}</dd></div>
                    <div><dt>Florida lien outer limit</dt><dd>{formatDate(floridaOuterLimit)}</dd></div>
                  </dl>
                  <p>Florida gives a recorded judgment lien an initial 10-year period. A timely rerecording may extend it, but never beyond 20 years from judgment entry. We confirm later filings, satisfaction, authority, and the remaining protection period before payment.</p>
                </div>}
                <p className="portal-judgment-details__limitations"><strong>Important:</strong> This review does not establish current balance, ownership, validity, priority, collectability, legal eligibility, or successful recording. Payment does not itself extend a judgment. JudgmentLock is not a law firm and does not provide legal advice.</p>
              </div>
            </details>
          </div>
        </section>

        <section className="portal-assurance portal-assurance--simple" id="deliverables">
          <div className="shell portal-shell">
            <div className="portal-assurance__heading">
              <div>
                <p className="eyebrow"><span /> Simple from here</p>
                <h2>Three steps. We handle the hard parts.</h2>
              </div>
              <p>You stay in control while we prepare the documents, pay the ordinary filing costs, submit the filing, and keep you updated.</p>
            </div>
            <div className="portal-timeline">
              <article><span>01</span><div><strong>Confirm the details</strong><p>Verify your email and confirm you’re authorized to act for the creditor. We review everything before payment.</p></div></article>
              <article><span>02</span><div><strong>We file for you</strong><p>We prepare the documents, coordinate signatures and notarization, submit the filing, and track it.</p></div></article>
              <article><span>03</span><div><strong>Receive your proof</strong><p>We send you the official recorded document and keep a copy in your account.</p></div></article>
            </div>
            <div className="portal-detail-grid">
              <details>
                <summary>What is included in the $495 fee? <span>+</span></summary>
                <p>Ordinary government filing and recording fees, certification, notarization, shipping or courier, e-recording costs, document preparation, submission, tracking, and completed proof.</p>
              </details>
              <details>
                <summary>What if you can’t complete the filing? <span>+</span></summary>
                <p>If we determine that we cannot complete the filing, we tell you before payment whenever possible. If new information prevents completion later, the cancellation and refund terms shown before checkout apply.</p>
              </details>
              <details>
                <summary>Is JudgmentLock a law firm? <span>+</span></summary>
                <p>No. JudgmentLock is a filing service, not a law firm. We handle uncontested administrative filings but do not provide legal advice or represent you in court. If your judgment needs an attorney, we tell you before payment.</p>
              </details>
              <details>
                <summary>How long does the process take? <span>+</span></summary>
                <p>Most filings are completed within 2–4 weeks after we receive the signed and notarized documents. Court and recorder processing times vary, and we’ll keep you updated in your account.</p>
              </details>
            </div>
          </div>
        </section>

        {matter.canStartIntake && <section className="portal-final-cta">
          <div className="shell portal-shell">
            <p className="eyebrow"><span /> Ready when you are</p>
            <h2>{matter.deadlineApplicable ? "Protect your lien before the deadline." : "Let us handle the filing from here."}</h2>
            <p>Enter your email to get started. The next step is free, and no payment is required.</p>
            <a className="button button--gold" href="/portal/start">{primaryAction} <ArrowIcon /></a>
            <small><LockIcon /> Private and secure. No payment yet.</small>
          </div>
        </section>}
      </main>
      <footer className="portal-footer"><div className="shell portal-shell"><span>JudgmentLock</span><p>PO Box 706 · East Olympia, WA 98540</p><a href="mailto:support@judgementlock.com">support@judgementlock.com</a></div></footer>
    </div>
  );
}

function PortalStartPage() {
  const checkoutCancelled = new URLSearchParams(window.location.search).get("checkout") === "cancelled";
  const verificationInvalid = new URLSearchParams(window.location.search).get("verification") === "invalid";
  const [payload, setPayload] = useState<PortalPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [paymentStep, setPaymentStep] = useState(false);

  useEffect(() => {
    fetch("/api/portal", { headers: { Accept: "application/json" } })
      .then(async (response) => {
        const body = await response.json() as PortalPayload & { error?: string };
        if (!response.ok || !body.matter) throw new Error(body.error || "expired");
        setPayload(body);
      })
      .catch(() => setExpired(true))
      .finally(() => setLoading(false));
  }, []);

  async function exitSession() {
    await fetch("/api/portal/logout", { method: "POST" });
    window.location.assign("/");
  }

  if (loading) return <div className="portal-loading"><Brand /><span>Opening your secure next step…</span></div>;
  if (expired || !payload) return (
    <div className="portal-expired">
      <Brand />
      <p className="eyebrow"><span /> Session ended</p>
      <h1>Re-enter the reference from your notice.</h1>
      <p>Private sessions expire automatically. No personal or payment information is available from this page.</p>
      <a className="button button--ink" href="/#notice">Open my notice <ArrowIcon /></a>
    </div>
  );

  const matter = payload.matter;
  return (
    <div className="portal-page portal-start-page">
      <PortalHeader onExit={exitSession} />
      <main>
        <div className="shell portal-shell portal-action" id="next-step">
          <a className="portal-back-link" href="/portal">← Back to judgment details</a>
          <PortalProgress verified={payload.verified} accountAuthenticated={payload.accountAuthenticated} paymentStep={paymentStep} />
          {checkoutCancelled && <div className="checkout-cancelled" role="status"><strong>No payment was taken.</strong> You returned from secure checkout and can continue whenever you are ready.</div>}
          {verificationInvalid && <div className="checkout-cancelled" role="status"><strong>That secure link is no longer valid.</strong> Request a new link below.</div>}
          {!matter.canStartIntake
            ? <section className="portal-step-card"><p className="eyebrow"><span /> Review complete</p><h2>This filing is not available online.</h2><p>Contact support if you have questions about the record we found.</p></section>
            : payload.accountAuthenticated
              ? <AuthorityIntake matter={matter} emailMasked={payload.emailMasked} paymentStep={paymentStep} onPaymentStepChange={setPaymentStep} />
              : payload.verified
                ? <AccountSetup accountExists={payload.accountExists} emailMasked={payload.emailMasked} onAuthenticated={() => setPayload((current) => current ? { ...current, accountAuthenticated: true, accountExists: true } : current)} />
                : sentTo
                  ? <CheckEmail masked={sentTo} onChange={() => setSentTo(null)} />
                  : <VerificationStep onVerifiedState={setSentTo} />}
        </div>
      </main>
      <footer className="portal-footer"><div className="shell portal-shell"><span>JudgmentLock</span><p>PO Box 706 · East Olympia, WA 98540</p><a href="mailto:support@judgementlock.com">support@judgementlock.com</a></div></footer>
    </div>
  );
}

function ProcessSection() {
  return (
    <section className="section process" id="how-it-works">
      <div className="shell">
        <div className="section-heading">
          <p className="eyebrow"><span /> How JudgmentLock helps</p>
          <h2>We take the filing off your plate.</h2>
          <p>From the first record check through the completed recording, we keep the work moving and keep you updated.</p>
        </div>
        <div className="steps">
          <article><span>01</span><h3>We watch the deadline</h3><p>We monitor county records and identify judgments approaching a filing deadline.</p></article>
          <article><span>02</span><h3>We check the details</h3><p>We review the judgment, later filings, creditor information, deadline, and county requirements before checkout.</p></article>
          <article><span>03</span><h3>You confirm, sign, and notarize</h3><p>You verify your authority and current information, then complete any required signature and notarization steps.</p></article>
          <article><span>04</span><h3>We handle the rest</h3><p>We handle the documents and fees, submit the filing, track it through the recorder, and send you the completed evidence.</p></article>
        </div>
      </div>
    </section>
  );
}

function FirmSection() {
  return (
    <section className="section firm-section" id="for-firms">
      <div className="shell firm-grid">
        <div className="firm-art" aria-hidden="true">
          <div className="record-sheet record-sheet--back"><span /></div>
          <div className="record-sheet">
            <div className="record-sheet__head"><span>JL</span><strong>Deadline review</strong></div>
            <div className="record-line record-line--long" />
            <div className="record-line" />
            <div className="record-line record-line--medium" />
            <div className="record-stamp">Verified<br />source trail</div>
          </div>
        </div>
        <div>
          <p className="eyebrow eyebrow--light"><span /> For collection counsel</p>
          <h2>Find the matters your calendar never knew existed.</h2>
          <p>JudgmentLock identifies approaching deadlines in county records, resolves obvious satisfactions and later filings, and delivers evidence-backed matters before the window closes.</p>
          <ul className="firm-list">
            <li><CheckIcon /><span><strong>County exclusivity available</strong> for qualified collection firms</span></li>
            <li><CheckIcon /><span><strong>30 / 60 / 90-day feeds</strong> with source links and routing data</span></li>
            <li><CheckIcon /><span><strong>Portfolio monitoring</strong> for judgments you already manage</span></li>
          </ul>
          <a className="button button--outline-light" href="mailto:firms@judgementlock.com?subject=JudgmentLock%20law%20firm%20access">Request firm access <ArrowIcon /></a>
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const faqs = [
    ["Why did I receive a JudgmentLock notice?", "We found a public court record showing that a judgment lien may be approaching a filing deadline. Your notice reference opens the exact record we found. Receiving a notice does not obligate you to use JudgmentLock or create a charge."],
    ["What happens when I enter my reference?", "You first see the matched public record and apparent deadline at no cost. If you choose to continue, you verify your email, create a secure account, and confirm that you are authorized to act for the creditor. We check the judgment and county requirements before checkout; final acceptance depends on current facts and the receiving office."],
    ["What does JudgmentLock handle for me?", "We check the public record and filing path, coordinate the required court copies and creditor paperwork, cover the required filing-related fees, submit and track the filing, and return the completed recording evidence."],
    ["Is $495 really the total price?", "For an eligible standard filing, $495 is the total price. It includes our service and the ordinary government filing, certification, notarization, shipping or courier, and e-recording costs. We never add a charge without your approval, and we never take a percentage of the judgment."],
    ["How long does the process take?", "Most accepted matters are completed within 2–4 weeks after we receive all required signed and notarized documents. County processing, rejection, or correction requests can take longer. Your account shows the current status throughout the process."],
    ["What do I receive when the work is complete?", "You receive a completion record showing what was reviewed and submitted, plus the official recorded document after acceptance. If the recorder rejects the filing, we document the rejection and next status instead."],
    ["What if my judgment is not eligible?", "We screen for a supported administrative path before checkout. If the matter does not pass that screening, you are not charged. If an issue appears after payment, the cancellation and refund terms shown before checkout apply."],
    ["Why do you need my current creditor information?", "Recording requirements can include the creditor's current name, address, and authority to act. We collect this information only after email verification and use it to prepare and submit the required paperwork."],
    ["Does JudgmentLock own or collect my judgment?", "No. You keep ownership and control of your judgment. JudgmentLock handles the filing service and never takes a percentage of the judgment or any money you may collect."],
    ["Does this guarantee that I can collect the judgment?", "No. Our service handles the recording workflow, not collection or enforcement. A judgment can have legal, payment, bankruptcy, assignment, homestead, or other issues that require separate review."],
    ["Is JudgmentLock a law firm?", "No. JudgmentLock is not a law firm and does not provide legal advice or represent you in court. Questions about enforcement, validity, priority, or legal strategy should be directed to licensed counsel."],
  ];
  return (
    <section className="section faq" id="faq">
      <div className="shell faq-grid">
        <div><p className="eyebrow"><span /> Common questions</p><h2>What to know before you begin.</h2></div>
        <div>{faqs.map(([question, answer]) => <details key={question}><summary>{question}<span>+</span></summary><p>{answer}</p></details>)}</div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer>
      <div className="shell footer-top">
        <div><Brand compact /><p>Know the deadline. Verify the record. Preserve the lien.</p><address className="footer-address"><span>JudgmentLock</span><span>PO Box 706</span><span>East Olympia, WA 98540</span><a href="mailto:support@judgementlock.com">support@judgementlock.com</a></address></div>
        <div><strong>Service</strong><a href="/services">All services</a><a href="/#how-it-works">How it works</a><a href="/firms">For law firms</a><a href="/services">Pricing</a></div>
        <div><strong>Company</strong><a href="/privacy">Privacy</a><a href="/terms">Terms</a><a href="mailto:support@judgementlock.com">Contact</a></div>
      </div>
      <div className="shell footer-bottom"><span>© {new Date().getFullYear()} JudgmentLock at judgmentlock.com</span><p>JudgmentLock is not a law firm and does not provide legal advice or representation.</p></div>
    </footer>
  );
}

function ServicesPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Judgment filing services | JudgmentLock";
    return () => { document.title = previousTitle; };
  }, []);

  const services = [
    {
      number: "01",
      title: "Rerecord a judgment lien",
      price: "$495",
      summary: "Rerecord an eligible judgment to extend an existing real-property lien before its deadline.",
      detail: "We screen the filing path, coordinate the required court copies and creditor paperwork, cover the ordinary filing costs, submit the documents, and send you the completed evidence.",
      includes: ["Deadline and record review", "Required copies and paperwork", "Filing, tracking, and completed evidence"],
    },
    {
      number: "02",
      title: "Record in another county",
      price: "$495",
      summary: "Put your judgment on record in another county within the same state.",
      detail: "Where county-level recording is available, we confirm the correct filing office, obtain the required documents, pay the ordinary fees, track acceptance, and return the official recording.",
      includes: ["County filing path confirmed", "Required documents coordinated", "Official recorded document delivered"],
    },
    {
      number: "03",
      title: "Register in another state",
      price: "$895",
      summary: "Register an eligible judgment in another state when the debtor has moved.",
      detail: "We coordinate the certified judgment, destination-state paperwork, required notice and ordinary filing costs, then track the matter through acceptance and return the completed court record.",
      includes: ["Destination-state path confirmed", "Certified copies, notice, and filing coordinated", "Accepted court record delivered"],
    },
  ];

  return (
    <>
      <Header />
      <main className="services-page">
        <section className="services-hero">
          <div className="services-hero__wash" aria-hidden="true" />
          <div className="shell services-hero__inner">
            <p className="eyebrow"><span /> Judgment filing services</p>
            <h1>Keep your judgment working—wherever the debtor goes.</h1>
            <p className="services-hero__lede">Whether a deadline is approaching or the debtor has moved, JudgmentLock handles the documents, ordinary fees, filing, tracking, and completed evidence for one clear price.</p>
            <div className="services-hero__checks">
              <span><CheckIcon /> Flat, all-inclusive pricing</span>
              <span><CheckIcon /> No percentage of your judgment</span>
              <span><CheckIcon /> Filing path screened before checkout</span>
            </div>
            <div className="services-hero__actions">
              <a className="button button--ink" href="/#lookup">Check my notice reference <ArrowIcon /></a>
              <a className="services-text-link" href="mailto:support@judgementlock.com?subject=Question%20about%20a%20JudgmentLock%20service">Ask about a service <ArrowIcon /></a>
            </div>
          </div>
        </section>

        <section className="section services-list" aria-labelledby="services-title">
          <div className="shell">
            <div className="services-heading">
              <div><p className="eyebrow"><span /> Choose what you need</p><h2 id="services-title">One filing or the complete path.</h2></div>
              <p>You keep ownership and control of your judgment. We take the filing work off your plate and keep you updated from review through completion.</p>
            </div>
            <div className="service-cards">
              {services.map((service) => (
                <article className="service-card" key={service.number}>
                  <div className="service-card__top"><span>{service.number}</span><strong>{service.price}</strong></div>
                  <h3>{service.title}</h3>
                  <p className="service-card__summary">{service.summary}</p>
                  <p>{service.detail}</p>
                  <ul>{service.includes.map((item) => <li key={item}><CheckIcon /> {item}</li>)}</ul>
                  <small>All ordinary filing-related costs included. Available for supported, uncontested filing paths.</small>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section service-bundles" aria-labelledby="bundles-title">
          <div className="shell service-bundles__grid">
            <div className="service-bundles__copy">
              <p className="eyebrow eyebrow--light"><span /> When you need both</p>
              <h2 id="bundles-title">Bundle the filings. Keep one clear price.</h2>
              <p>If your judgment lien needs rerecording before another filing, we coordinate both steps as one service and keep the entire matter in one account.</p>
            </div>
            <div className="bundle-cards">
              <article><div><span>Lien rerecording + another county</span><strong>$795</strong></div><p>Rerecord the eligible judgment lien and record it in one additional county within the same state.</p></article>
              <article><div><span>Lien rerecording + another state</span><strong>$1,295</strong></div><p>Rerecord the eligible judgment lien and register the judgment in one destination state.</p></article>
              <small>Each price includes the standard government, certification, notice, notarization, shipping, courier, and electronic-filing costs needed for an eligible filing.</small>
            </div>
          </div>
        </section>

        <section className="section services-process" aria-labelledby="services-process-title">
          <div className="shell">
            <div className="section-heading">
              <p className="eyebrow"><span /> What happens next</p>
              <h2 id="services-process-title">You confirm the facts. We handle the filing.</h2>
              <p>Every service follows the same straightforward path.</p>
            </div>
            <div className="steps services-steps">
              <article><span>01</span><h3>We check the path</h3><p>We review the judgment, filing location, deadline, later court activity, and the documents the receiving office requires.</p></article>
              <article><span>02</span><h3>You confirm, sign, and notarize</h3><p>You verify your authority and current information, then sign and notarize any creditor paperwork required for the filing.</p></article>
              <article><span>03</span><h3>We submit and track</h3><p>We coordinate the copies, ordinary fees, notice, delivery, and follow-up with the court or recorder.</p></article>
              <article><span>04</span><h3>You receive the result</h3><p>Your account shows each step, and you receive the accepted court or recording evidence when the work is complete.</p></article>
            </div>
          </div>
        </section>

        <section className="section service-boundary">
          <div className="shell service-boundary__card">
            <div><p className="eyebrow"><span /> Clear boundaries</p><h2>Administrative filing help—not legal representation.</h2></div>
            <div><p>JudgmentLock handles supported, uncontested filing workflows. If a matter requires a lawsuit, contested hearing, legal argument, or court representation, it is not eligible for our administrative service.</p><p>We screen the record for a supported administrative filing path before checkout. Final acceptance depends on current facts, the information you provide, and the receiving office. Refunds after payment follow the Terms shown before checkout.</p><a href="mailto:support@judgementlock.com?subject=JudgmentLock%20service%20eligibility">Ask about my judgment <ArrowIcon /></a></div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function LegalPage({ kind }: { kind: "privacy" | "terms" | "florida" }) {
  const privacy = kind === "privacy";
  const florida = kind === "florida";
  const sections = privacy ? PRIVACY_SECTIONS : florida ? FLORIDA_ADDENDUM_SECTIONS : TERMS_SECTIONS;
  const effectiveDate = privacy ? PRIVACY_EFFECTIVE_DATE : florida ? FLORIDA_ADDENDUM_EFFECTIVE_DATE : TERMS_EFFECTIVE_DATE;
  const version = privacy ? PRIVACY_VERSION : florida ? FLORIDA_ADDENDUM_VERSION : TERMS_VERSION;
  const title = privacy ? "Privacy policy" : florida ? "Florida service addendum" : "Terms of service";
  return (
    <div className="legal-page">
      <Header />
      <main className="shell legal-content">
        <p className="eyebrow"><span /> {privacy ? "Privacy" : florida ? "State terms" : "Terms"}</p>
        <h1>{title}</h1>
        <p className="legal-date">Effective {effectiveDate} · Version {version}</p>
        {kind === "terms" && <p className="legal-date">Florida services are also governed by the <a href="/terms/florida">Florida Service Addendum</a>.</p>}
        {florida && <p className="legal-date">This Addendum supplements the <a href="/terms">JudgmentLock Terms of Service</a>.</p>}
        {sections.map((section) => <section key={section.title}><h2>{section.title}</h2><p>{section.body}</p></section>)}
      </main>
      <Footer />
    </div>
  );
}

function SuccessPage() {
  return (
    <div className="legal-page">
      <Header />
      <main className="shell success-card">
        <div className="success-check"><CheckIcon /></div>
        <p className="eyebrow"><span /> Payment submitted</p>
        <h1>Thank you for your order.</h1>
        <p>We’ll email your confirmation as soon as Stripe confirms the payment. Your dashboard has your order status, judgment records, and receipt. Payment alone does not extend the lien.</p>
        <a className="button button--ink" href="/account">View my dashboard <ArrowIcon /></a>
      </main>
      <Footer />
    </div>
  );
}

function FirmPage() {
  return (
    <>
      <Header />
      <main className="firm-page">
        <FirmSection />
        <section className="section firm-detail">
          <div className="shell firm-detail__grid">
            <div><p className="eyebrow"><span /> Evidence-backed intake</p><h2>Qualified matters, not a county firehose.</h2><p>Each feed is designed around approaching deadlines, later satisfaction and rerecording checks, source links, creditor identity, and routing evidence. Coverage is offered only where the underlying public sources and deadline rules have been validated.</p></div>
            <div className="firm-detail__card"><strong>Firm access includes</strong><ul><li><CheckIcon /> 30 / 60 / 90-day matter windows</li><li><CheckIcon /> Official-record source links</li><li><CheckIcon /> Later-filing and satisfaction screens</li><li><CheckIcon /> County-specific coverage notes</li></ul><a className="button button--ink button--wide" href="mailto:firms@judgementlock.com?subject=JudgmentLock%20law%20firm%20access">Request firm access <ArrowIcon /></a></div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function CaseConversation({ orderId }: { orderId: string }) {
  const [messages, setMessages] = useState<CaseMessage[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    const response = await fetch(`/api/account/orders/${orderId}/messages`);
    const body = await response.json() as { messages?: CaseMessage[]; error?: string };
    if (!response.ok) throw new Error(body.error || "The conversation could not be opened.");
    setMessages(body.messages || []); setLoaded(true);
  }

  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const response = await fetch(`/api/account/orders/${orderId}/messages`, { method: "POST", body: new FormData(event.currentTarget) });
      const body = await response.json() as { created?: boolean; error?: string };
      if (!response.ok || !body.created) throw new Error(body.error || "Your message could not be sent.");
      event.currentTarget.reset(); await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Your message could not be sent."); }
    finally { setBusy(false); }
  }

  async function openAttachment(attachment: CaseAttachment) {
    const response = await fetch(`/api/account/message-attachments/${attachment.id}/link`, { method: "POST" });
    const body = await response.json() as { url?: string; error?: string };
    if (!response.ok || !body.url) return window.alert(body.error || "The file is not available yet.");
    window.location.assign(body.url);
  }

  return <details className="case-conversation" onToggle={(event) => {
    if (event.currentTarget.open && !loaded) load().catch((reason) => setError(reason instanceof Error ? reason.message : "The conversation could not be opened."));
  }}>
    <summary><span><strong>Messages and files</strong><small>Talk with JudgmentLock about this judgment</small></span><ArrowIcon /></summary>
    <div className="case-conversation__body">
      {!loaded && !error && <p className="case-conversation__empty">Opening conversation…</p>}
      {loaded && messages.length === 0 && <p className="case-conversation__empty">No messages yet. Send a question or securely upload a file for this judgment.</p>}
      {messages.map((message) => <article className={`case-message case-message--${message.senderRole}`} key={message.id}>
        <header><strong>{message.senderRole === "customer" ? "You" : "JudgmentLock support"}</strong><small>{formatDate(message.createdAt)}</small></header>
        {message.body && <p>{message.body}</p>}
        {message.attachments.map((attachment) => <button key={attachment.id} type="button" disabled={attachment.scanStatus !== "clean"} onClick={() => openAttachment(attachment)}>
          <LockIcon /><span><strong>{attachment.name}</strong><small>{attachment.scanStatus === "clean" ? "Secure file" : attachment.scanStatus === "rejected" ? "File rejected" : "Security review pending"}</small></span>
        </button>)}
      </article>)}
      <form className="case-message-form" onSubmit={send}>
        <label><span>Message</span><textarea name="message" rows={4} maxLength={4000} placeholder="Ask a question or explain the files you are sending." /></label>
        <label><span>Attach files <small>PDF, PNG, or JPG · up to 3 files</small></span><input name="files" type="file" multiple accept="application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg" /></label>
        {error && <p className="form-error">{error}</p>}
        <button className="button button--ink" type="submit" disabled={busy}>{busy ? "Sending…" : "Send securely"}</button>
      </form>
    </div>
  </details>;
}

function AccountAddNoticePage() {
  const [email, setEmail] = useState("");
  useEffect(() => {
    fetch("/api/account").then(async (response) => {
      if (response.status === 401) return window.location.assign("/login");
      const body = await response.json() as AccountPayload;
      setEmail(body.account.email);
    });
  }, []);
  return <div className="account-page">
    <header className="account-header"><div className="shell account-header__inner"><Brand /><div><span>{email}</span><a href="/account">Back to dashboard</a></div></div></header>
    <main className="shell account-add-main">
      <p className="eyebrow"><span /> Add a judgment</p>
      <h1>Open another notice.</h1>
      <p>Enter the reference from the notice. Because you’re signed in, the judgment will be added to this account and you can review its details before checkout.</p>
      <ReferenceLookup formId="account-notice" inputId="account-reference" buttonLabel="Review notice" help="The code begins with JL- and appears on the notice." />
    </main>
  </div>;
}

function AccountPage() {
  const [payload, setPayload] = useState<AccountPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/account", { headers: { Accept: "application/json" } })
      .then(async (response) => {
        if (response.status === 401) return window.location.assign("/login");
        const body = await response.json() as AccountPayload & { error?: string };
        if (!response.ok || !body.account) throw new Error(body.error || "Account unavailable.");
        setPayload(body);
      })
      .finally(() => setLoading(false));
  }, []);

  async function signOut() {
    await fetch("/api/account/logout", { method: "POST" });
    window.location.assign("/login");
  }

  async function downloadDocument(documentId: string) {
    const response = await fetch(`/api/account/documents/${documentId}/link`, { method: "POST" });
    const body = await response.json() as { url?: string; error?: string };
    if (!response.ok || !body.url) return window.alert(body.error || "The document could not be opened.");
    window.location.assign(body.url);
  }

  async function openSourceDocument(accessId: string) {
    const response = await fetch(`/api/account/matters/${accessId}/document`, { method: "POST" });
    const body = await response.json() as { url?: string; error?: string };
    if (!response.ok || !body.url) return window.alert(body.error || "The saved judgment could not be opened.");
    window.location.assign(body.url);
  }

  async function openReceipt(orderId: string) {
    const response = await fetch(`/api/account/orders/${orderId}/receipt`, { method: "POST" });
    const body = await response.json() as { url?: string; error?: string };
    if (!response.ok || !body.url) return window.alert(body.error || "The payment receipt could not be opened.");
    window.location.assign(body.url);
  }

  async function cancelMonitoring(accessId: string) {
    if (!window.confirm("Cancel annual debtor monitoring at the end of the current paid year?")) return;
    const response = await fetch("/api/account/monitoring/cancel", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ accessId }),
    });
    const body = await response.json() as { canceled?: boolean; error?: string };
    if (!response.ok || !body.canceled) return window.alert(body.error || "Monitoring could not be canceled.");
    window.location.reload();
  }

  if (loading) return <div className="portal-loading"><Brand /><span>Opening your account…</span></div>;
  if (!payload) return <div className="portal-expired"><Brand /><h1>Your account could not be opened.</h1><a className="button button--ink" href="/login">Return to sign in</a></div>;

  return (
    <div className="account-page">
      <header className="account-header"><div className="shell account-header__inner"><Brand /><div><span>{payload.account.email}</span><button type="button" onClick={signOut}>Sign out</button></div></div></header>
      <main className="shell account-main">
        <div className="account-heading"><div><p className="eyebrow"><span /> Client account</p><h1>Your judgment dashboard.</h1><p>Monitor linked judgments, filing activity, messages, and completed recording evidence.</p></div><a className="button button--ink" href="/account/add">Add another notice <ArrowIcon /></a></div>
        <section className="account-matters" aria-labelledby="account-matters-title">
          <h2 id="account-matters-title">Linked judgments</h2>
          {payload.matters.length === 0 ? <div className="account-empty"><h3>No judgments are linked yet.</h3><p>Enter the reference from a JudgmentLock notice to review it inside your account.</p><a className="button button--gold" href="/account/add">Open a notice</a></div> : payload.matters.map((matter) => (
            <details className="account-matter" key={`${matter.caseNumber}-${matter.orderId || "record"}`}>
              <summary className="account-matter__summary">
                <span className="account-matter__identity"><small>{matter.county} County, {matter.state}</small><strong>{matter.creditor}</strong><span>v. {matter.debtor}</span></span>
                <span className="account-matter__summary-meta"><span><small>Court case</small><strong>{matter.caseNumber}</strong></span><span><small>{matter.deadlineApplicable ? "Apparent deadline" : "Record date"}</small><strong>{formatDate(matter.deadlineApplicable ? matter.deadlineAt : matter.recordedAt)}</strong></span></span>
                <span className={`account-status${matter.orderStatus ? " account-status--active" : ""}`}><i /><span>{matter.statusLabel}{matter.unreadMessages > 0 ? ` · ${matter.unreadMessages} new` : ""}</span></span>
                <span className="account-matter__toggle" aria-hidden="true"><svg viewBox="0 0 20 20"><path d="m5 7.5 5 5 5-5" /></svg></span>
              </summary>
              <div className="account-matter__body">
                <dl><div><dt>Court case</dt><dd>{matter.caseNumber}</dd></div><div><dt>{matter.deadlineApplicable ? "Apparent deadline" : "Record date"}</dt><dd>{formatDate(matter.deadlineApplicable ? matter.deadlineAt : matter.recordedAt)}</dd></div><div><dt>Order</dt><dd>{matter.orderId || "Not started"}</dd></div></dl>
                <div className="account-matter__status"><div><CheckIcon /></div><span><strong>{matter.statusLabel}</strong><p>{matter.statusDetail}</p>{matter.orderUpdatedAt && <small>Updated {formatDate(matter.orderUpdatedAt)}</small>}</span></div>
                <div className="account-records">
                  <h4>Judgment record</h4>
                  <div>
                    {matter.sourceDocumentAvailable && <button type="button" onClick={() => openSourceDocument(matter.accessId)}><LockIcon /><span><strong>{matter.sourceDocumentName || "Saved court-filed judgment"}</strong><small>Secure copy saved by JudgmentLock</small></span><ArrowIcon /></button>}
                    {matter.sourceDocumentUrl && <a href={matter.sourceDocumentUrl} target="_blank" rel="noreferrer"><span><strong>Original source document</strong><small>Opens the source website</small></span><ArrowIcon /></a>}
                    <a href={matter.sourceUrl} target="_blank" rel="noreferrer"><span><strong>Official public record</strong><small>Opens the county or court record</small></span><ArrowIcon /></a>
                    {matter.receiptAvailable && matter.orderId && <button type="button" onClick={() => openReceipt(matter.orderId!)}><span><strong>Payment receipt</strong><small>Opens your Stripe-hosted receipt</small></span><ArrowIcon /></button>}
                  </div>
                </div>
                {matter.events.length > 0 && <div className="account-timeline"><h4>Status history</h4>{matter.events.map((event, index) => <div key={`${event.createdAt}-${index}`}><i /><span><strong>{event.title}</strong>{event.detail && <p>{event.detail}</p>}<small>{formatDate(event.createdAt)}</small></span></div>)}</div>}
                {matter.documents.length > 0 && <div className="account-documents"><h4>Documents</h4>{matter.documents.map((document) => <button type="button" key={document.id} onClick={() => downloadDocument(document.id)}><LockIcon /><span><strong>{document.name}</strong><small>Secure PDF · {formatDate(document.createdAt)}</small></span><ArrowIcon /></button>)}</div>}
                {matter.monitoring && <div className="account-monitoring">
                  <div><span>Debtor monitoring</span><strong>{matter.monitoring.status === "active" ? "Active" : matter.monitoring.status === "cancel_at_period_end" ? "Ends after current year" : matter.monitoring.status.replaceAll("_", " ")}</strong></div>
                  <p>Monthly checks watch for a named individual debtor moving to another county or state. Exact skip-trace addresses stay private.</p>
                  {matter.monitoring.lastCheckedAt && <small>Last checked {formatDate(matter.monitoring.lastCheckedAt)}</small>}
                  {matter.monitoring.serviceEndsAt && <small>Ends automatically when the judgment expires on {formatDate(matter.monitoring.serviceEndsAt)}</small>}
                  {matter.monitoring.status === "active" && <button type="button" onClick={() => cancelMonitoring(matter.accessId)}>Cancel annual renewal</button>}
                </div>}
                {matter.orderId && !["creating_checkout", "checkout_created", "checkout_failed", "checkout_expired", "payment_failed"].includes(matter.orderStatus || "") && <CaseConversation orderId={matter.orderId} />}
              </div>
            </details>
          ))}
        </section>
        <p className="account-support">Use the secure conversation inside each purchased judgment for questions and files. General support remains available at <a href="mailto:support@judgementlock.com">support@judgementlock.com</a>.</p>
      </main>
    </div>
  );
}

function StaffLoginPage() {
  return (
    <div className="client-login-page">
      <Header />
      <main className="shell client-login-main">
        <div className="client-login-shell">
          <div className="client-login-copy"><p className="eyebrow"><span /> Staff operations</p><h1>Open the fulfillment queue.</h1><p>Use the JudgmentLock account that has been granted staff access.</p></div>
          <div className="client-login-panel"><h2>Staff sign in</h2><LoginForm onSuccess={() => window.location.assign("/staff")} /></div>
        </div>
      </main>
    </div>
  );
}

function labelStatus(status: string) {
  return status.split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
}

function StaffPage() {
  const [staff, setStaff] = useState<{ email: string; role: "operator" | "admin" } | null>(null);
  const [orders, setOrders] = useState<StaffOrderSummary[]>([]);
  const [alerts, setAlerts] = useState<StaffAlert[]>([]);
  const [dashboard, setDashboard] = useState<StaffDashboard | null>(null);
  const [selected, setSelected] = useState<StaffOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [statusForm, setStatusForm] = useState({ status: "", title: "", detail: "", visibleToClient: true });
  const [note, setNote] = useState("");

  async function api<T>(url: string, init?: RequestInit) {
    const response = await fetch(url, init);
    if (response.status === 401) {
      window.location.assign("/staff/login");
      throw new Error("Staff session expired.");
    }
    const body = await response.json() as T & { error?: string };
    if (!response.ok) throw new Error(body.error || "The request could not be completed.");
    return body;
  }

  async function loadOrders() {
    const body = await api<{ orders: StaffOrderSummary[] }>("/api/staff/orders");
    setOrders(body.orders);
    return body.orders;
  }

  async function loadAlerts() {
    const body = await api<{ alerts: StaffAlert[] }>("/api/staff/alerts");
    setAlerts(body.alerts);
    return body.alerts;
  }

  async function loadDashboard() {
    const body = await api<StaffDashboard>("/api/staff/dashboard");
    setDashboard(body); return body;
  }

  async function refreshOperations() {
    setBusy(true); setError("");
    try {
      const [alertBody] = await Promise.all([
        api<{ alerts: StaffAlert[] }>("/api/staff/alerts/refresh", { method: "POST" }),
        loadOrders(), loadDashboard(),
      ]);
      setAlerts(alertBody.alerts);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Operations could not be refreshed."); }
    finally { setBusy(false); }
  }

  async function openOrder(id: string) {
    const body = await api<StaffOrderDetail>(`/api/staff/orders/${id}`);
    setSelected(body);
    setStatusForm({ status: body.nextStatuses[0] || "", title: "", detail: "", visibleToClient: true });
  }

  useEffect(() => {
    Promise.all([
      api<{ staff: { email: string; role: "operator" | "admin" } }>("/api/staff/session"),
      loadOrders(),
      loadAlerts(),
      loadDashboard(),
    ]).then(([session, list]) => {
      setStaff(session.staff);
      if (list[0]) openOrder(list[0].id);
    }).catch((reason) => setError(reason instanceof Error ? reason.message : "Staff queue unavailable."))
      .finally(() => setLoading(false));
  }, []);

  async function updateStatus(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setBusy(true); setError("");
    try {
      await api(`/api/staff/orders/${selected.order.id}/status`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(statusForm) });
      await Promise.all([loadOrders(), openOrder(selected.order.id)]);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Status update failed."); }
    finally { setBusy(false); }
  }

  async function addNote(event: FormEvent) {
    event.preventDefault();
    if (!selected || !note.trim()) return;
    setBusy(true); setError("");
    try {
      await api(`/api/staff/orders/${selected.order.id}/notes`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ note, visibleToClient: false }) });
      setNote(""); await openOrder(selected.order.id);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Note could not be saved."); }
    finally { setBusy(false); }
  }

  async function uploadDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    setBusy(true); setError("");
    try {
      await api(`/api/staff/orders/${selected.order.id}/documents`, { method: "POST", body: new FormData(event.currentTarget) });
      event.currentTarget.reset(); await openOrder(selected.order.id);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Document upload failed."); }
    finally { setBusy(false); }
  }

  async function sendCaseMessage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    setBusy(true); setError("");
    try {
      await api(`/api/staff/orders/${selected.order.id}/messages`, { method: "POST", body: new FormData(event.currentTarget) });
      event.currentTarget.reset(); await Promise.all([openOrder(selected.order.id), loadDashboard()]);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Message could not be sent."); }
    finally { setBusy(false); }
  }

  async function openCaseAttachment(attachmentId: string) {
    try {
      const body = await api<{ url: string }>(`/api/staff/message-attachments/${attachmentId}/link`, { method: "POST" });
      window.location.assign(body.url);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "File could not be opened."); }
  }

  async function reviewCaseAttachment(attachmentId: string, scanStatus: "clean" | "rejected") {
    if (!selected) return;
    const scanProvider = window.prompt("Malware scanner and result/reference:");
    if (!scanProvider) return;
    setBusy(true); setError("");
    try {
      await api(`/api/staff/message-attachments/${attachmentId}/review`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scanStatus, scanProvider }) });
      await Promise.all([openOrder(selected.order.id), loadDashboard()]);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "File review failed."); }
    finally { setBusy(false); }
  }

  async function reviewDocument(documentId: string, scanStatus: "clean" | "rejected") {
    if (!selected) return;
    const scanProvider = window.prompt("Malware scanner and result/reference:");
    if (!scanProvider) return;
    setBusy(true); setError("");
    try {
      await api(`/api/staff/orders/${selected.order.id}/documents/${documentId}/review`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scanStatus, scanProvider, visibleToClient: scanStatus === "clean" }) });
      await openOrder(selected.order.id);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Document review failed."); }
    finally { setBusy(false); }
  }

  async function requestRefund() {
    if (!selected || !window.confirm(`Refund ${selected.order.id}? This submits a real Stripe refund in the current environment.`)) return;
    setBusy(true); setError("");
    try {
      await api(`/api/staff/orders/${selected.order.id}/refund`, { method: "POST" });
      await Promise.all([loadOrders(), openOrder(selected.order.id)]);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Refund failed."); }
    finally { setBusy(false); }
  }

  async function signOut() {
    await fetch("/api/account/logout", { method: "POST" });
    window.location.assign("/staff/login");
  }

  if (loading) return <div className="portal-loading"><Brand /><span>Opening fulfillment queue…</span></div>;
  return (
    <div className="staff-page">
      <header className="account-header"><div className="shell account-header__inner"><Brand /><div><span>{staff?.email} · {staff?.role}</span><button type="button" onClick={signOut}>Sign out</button></div></div></header>
      <main className="shell staff-main">
        <div className="staff-heading"><div><p className="eyebrow"><span /> Staff operations</p><h1>Admin dashboard</h1></div><button className="button button--ink" type="button" disabled={busy} onClick={refreshOperations}>Refresh operations</button></div>
        {error && <p className="staff-error">{error}</p>}
        {dashboard && <>
          <section className="admin-metrics" aria-label="Business metrics">
            <article><small>Judgments</small><strong>{dashboard.metrics.totalJudgments.toLocaleString()}</strong><span>{dashboard.metrics.noticeReady.toLocaleString()} notice-ready</span></article>
            <article><small>Active orders</small><strong>{dashboard.metrics.activeOrders.toLocaleString()}</strong><span>{dashboard.metrics.unreadMessages} unread messages</span></article>
            <article><small>Verified → paid</small><strong>{(dashboard.metrics.verifiedToPaidRate * 100).toFixed(1)}%</strong><span>{dashboard.metrics.paidOrders} paid / {dashboard.metrics.verifiedSessions} verified · 30 days</span></article>
            <article><small>Checkout conversion</small><strong>{(dashboard.metrics.checkoutConversionRate * 100).toFixed(1)}%</strong><span>{dashboard.metrics.paidOrders} paid / {dashboard.metrics.startedOrders} started · 30 days</span></article>
            <article><small>Letters sent</small><strong>{dashboard.metrics.lettersSent.toLocaleString()}</strong><span>PaperPilot-ready ledger</span></article>
            <article><small>Files to review</small><strong>{dashboard.metrics.filesPending.toLocaleString()}</strong><span>Customer and staff uploads</span></article>
          </section>
          <details className="admin-inventory">
            <summary><span><strong>All judgments</strong><small>{dashboard.judgments.length} loaded · status, notices, orders, and messages</small></span><ArrowIcon /></summary>
            <div className="admin-inventory__table" role="table" aria-label="Judgment inventory">
              <div role="row" className="admin-inventory__head"><span>Judgment</span><span>Deadline</span><span>Notice</span><span>Order</span><span>Messages</span></div>
              {dashboard.judgments.map((judgment) => <button type="button" role="row" key={judgment.reference} disabled={!judgment.orderId} onClick={() => judgment.orderId && openOrder(judgment.orderId)}>
                <span><strong>{judgment.creditor}</strong><small>v. {judgment.debtor} · {judgment.reference}</small></span>
                <span>{formatDate(judgment.deadlineAt)}</span><span>{labelStatus(judgment.noticeStatus)}</span>
                <span>{judgment.orderStatus ? labelStatus(judgment.orderStatus) : "No order"}</span><span>{judgment.unreadMessages ? `${judgment.unreadMessages} unread` : "—"}</span>
              </button>)}
            </div>
          </details>
        </>}
        {alerts.length > 0 && <section className="staff-alerts" aria-labelledby="operations-alerts-title"><header><div><span>Operational monitoring</span><h2 id="operations-alerts-title">Alerts</h2></div><small>{alerts.filter((alert) => alert.status === "open").length} open</small></header><div>{alerts.map((alert) => <article className={`staff-alert staff-alert--${alert.severity}${alert.status === "resolved" ? " is-resolved" : ""}`} key={alert.alert_key}><span>{alert.severity}</span><div><strong>{alert.summary}</strong><p>{alert.detected_count} detected · {labelStatus(alert.category)} · Last checked {formatDate(alert.last_detected_at)}</p></div><em>{alert.status}</em></article>)}</div></section>}
        <div className="staff-layout">
          <aside className="staff-queue" aria-label="Orders">
            {orders.length === 0 ? <p>No orders are in the queue.</p> : orders.map((order) => <button type="button" className={selected?.order.id === order.id ? "is-selected" : ""} key={order.id} onClick={() => openOrder(order.id)}><span><strong>{order.creditor}</strong><small>{order.id} · {order.county} County</small></span><em>{labelStatus(order.status)}</em></button>)}
          </aside>
          <section className="staff-detail">
            {!selected ? <div className="staff-empty"><h2>Select an order</h2><p>Choose an order from the queue to review its intake and update fulfillment.</p></div> : <>
              <header><div><small>{selected.order.id}</small><h2>{selected.order.creditor}</h2><p>v. {selected.order.debtor} · {selected.order.caseNumber}</p></div><span>{labelStatus(selected.order.status)}</span></header>
              <dl className="staff-facts"><div><dt>Representative</dt><dd>{selected.order.representativeName}<small>{selected.order.organizationName}</small></dd></div><div><dt>Contact</dt><dd><a href={`mailto:${selected.order.email}`}>{selected.order.email}</a><small>{selected.order.phone}</small></dd></div><div><dt>Creditor address</dt><dd className="address-block">{selected.order.creditorAddress}</dd></div><div><dt>Deadline</dt><dd>{formatDate(selected.order.deadlineAt)}<small>{selected.order.county} County, {selected.order.state}</small></dd></div><div><dt>Notarization route</dt><dd>{selected.order.notarizationPlan.status === "supported" ? labelStatus(selected.order.notarizationPlan.execution || "") : "Blocked"}<small>{selected.order.notarizationPlan.sessionType ? `OneNotary ${selected.order.notarizationPlan.sessionType} · ${selected.order.notarizationPlan.filingMedium}` : selected.order.notarizationPlan.reason}</small></dd></div><div><dt>Legal acceptance</dt><dd>{selected.order.termsVersion || "Legacy order"}<small>{selected.order.termsAcceptedAt ? formatDate(selected.order.termsAcceptedAt) : "No acceptance timestamp"}{selected.order.termsSha256 ? ` · ${selected.order.termsSha256.slice(0, 12)}…` : ""}{selected.order.stateAddendumVersion ? ` · ${selected.order.stateAddendumCode} ${selected.order.stateAddendumVersion}` : ""}{selected.order.stateAddendumSha256 ? ` · ${selected.order.stateAddendumSha256.slice(0, 12)}…` : ""}</small></dd></div></dl>
              <section className="staff-conversation"><header><div><h3>Customer conversation</h3><p>Case-specific messages and secure files.</p></div><span>{selected.messages.filter((message) => message.senderRole === "customer").length} customer messages</span></header>
                <div className="staff-conversation__messages">
                  {selected.messages.length === 0 && <p className="staff-conversation__empty">No messages yet.</p>}
                  {selected.messages.map((message) => <article className={`case-message case-message--${message.senderRole}`} key={message.id}><header><strong>{message.senderRole === "customer" ? selected.order.representativeName : "JudgmentLock support"}</strong><small>{formatDate(message.createdAt)}</small></header>{message.body && <p>{message.body}</p>}{message.attachments.map((attachment) => <div className="staff-case-file" key={attachment.id}><button type="button" disabled={attachment.scanStatus === "rejected"} onClick={() => openCaseAttachment(attachment.id)}><LockIcon /><span><strong>{attachment.name}</strong><small>{attachment.scanStatus} · {(attachment.byteSize / 1024).toFixed(0)} KB</small></span></button>{staff?.role === "admin" && attachment.scanStatus === "pending" && <span><button type="button" onClick={() => reviewCaseAttachment(attachment.id, "clean")}>Approve clean</button><button type="button" onClick={() => reviewCaseAttachment(attachment.id, "rejected")}>Reject</button></span>}</div>)}</article>)}
                </div>
                <form className="case-message-form" onSubmit={sendCaseMessage}><label><span>Reply</span><textarea name="message" rows={4} maxLength={4000} placeholder="Write a customer-facing message." /></label><label><span>Attach files <small>PDF, PNG, or JPG · files appear after security review</small></span><input name="files" type="file" multiple accept="application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg" /></label><button className="button button--ink" disabled={busy} type="submit">Send secure reply</button></form>
              </section>
              {selected.nextStatuses.length > 0 && <form className="staff-form" onSubmit={updateStatus}><h3>Advance fulfillment</h3><label><span>Next status</span><select required value={statusForm.status} onChange={(event) => setStatusForm({ ...statusForm, status: event.target.value })}>{selected.nextStatuses.map((status) => <option key={status} value={status}>{labelStatus(status)}</option>)}</select></label><label><span>Customer-facing title</span><input required maxLength={120} value={statusForm.title} onChange={(event) => setStatusForm({ ...statusForm, title: event.target.value })} /></label><label><span>Customer-facing detail</span><textarea required maxLength={500} rows={3} value={statusForm.detail} onChange={(event) => setStatusForm({ ...statusForm, detail: event.target.value })} /></label><label className="staff-check"><input type="checkbox" checked={statusForm.visibleToClient} onChange={(event) => setStatusForm({ ...statusForm, visibleToClient: event.target.checked })} /> Show in client account and email the customer</label><button className="button button--gold" disabled={busy} type="submit">Save status update</button></form>}
              <form className="staff-form" onSubmit={addNote}><h3>Internal note</h3><label><span>Note</span><textarea required maxLength={2000} rows={3} value={note} onChange={(event) => setNote(event.target.value)} /></label><button className="button button--ink" disabled={busy} type="submit">Add internal note</button></form>
              <section className="staff-docs"><h3>Completion documents</h3><form onSubmit={uploadDocument}><label><span>Document type</span><select name="documentType" required defaultValue="completion_packet"><option value="completion_packet">Completion packet</option><option value="recording_receipt">Recording receipt</option><option value="recorded_instrument">Recorded instrument</option><option value="supporting_document">Supporting document</option></select></label><label><span>PDF file</span><input name="file" type="file" accept="application/pdf,.pdf" required /></label><button className="button button--ink" disabled={busy} type="submit">Upload PDF</button></form>{selected.documents.map((document) => <div key={String(document.id)}><span><strong>{String(document.display_name)}</strong><small>{String(document.document_type)} · {String(document.scan_status)}</small></span>{staff?.role === "admin" && document.scan_status === "pending" && <span><button type="button" onClick={() => reviewDocument(String(document.id), "clean")}>Approve clean</button><button type="button" onClick={() => reviewDocument(String(document.id), "rejected")}>Reject</button></span>}</div>)}</section>
              <section className="staff-history"><h3>Order history</h3>{selected.events.map((event) => <div key={String(event.id)}><i /><span><strong>{String(event.title)}</strong><p>{event.detail ? String(event.detail) : labelStatus(String(event.status))}</p><small>{formatDate(String(event.created_at))}{Number(event.visible_to_client) === 1 ? " · Client visible" : " · Internal"}</small></span></div>)}{selected.notes.map((item) => <div key={String(item.id)}><i /><span><strong>Internal note</strong><p>{String(item.note)}</p><small>{formatDate(String(item.created_at))} · {String(item.author)}</small></span></div>)}</section>
              {staff?.role === "admin" && selected.order.stripePaymentIntentId && selected.order.status !== "refunded" && <div className="staff-danger"><div><strong>Payment controls</strong><p>Refund processing is separately disabled in production until launch approval.</p></div><button type="button" disabled={busy} onClick={requestRefund}>Issue full refund</button></div>}
            </>}
          </section>
        </div>
      </main>
    </div>
  );
}

export default function App() {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  const legalKind = useMemo(() => path === "/privacy" ? "privacy" : path === "/terms" ? "terms" : path === "/terms/florida" ? "florida" : null, [path]);
  if (legalKind) return <LegalPage kind={legalKind} />;
  if (path === "/portal") return <PortalPage />;
  if (path === "/portal/start") return <PortalStartPage />;
  if (path === "/login") return <ClientLoginPage />;
  if (path === "/forgot-password") return <ForgotPasswordPage />;
  if (path === "/reset-password") return <ResetPasswordPage />;
  if (path === "/account/add") return <AccountAddNoticePage />;
  if (path === "/account") return <AccountPage />;
  if (path === "/staff/login") return <StaffLoginPage />;
  if (path === "/staff") return <StaffPage />;
  if (path === "/services") return <ServicesPage />;
  if (path === "/firms") return <FirmPage />;
  if (path === "/renewal/success") return <SuccessPage />;
  return (
    <>
      <Header />
      <Hero />
      <ProcessSection />
      <FaqSection />
      <Footer />
    </>
  );
}
